import { useContext, useState } from 'react';

import styles from './magazines.module.css';

import { Issue } from '@/constants';
import { Skeleton } from '@shared/ui/skeleton';
import { TrackingContext } from '@shared/tracking';
import { PreviewLink, previewTransitionName } from '@shared/ui/preview-link';
import {
	useResponsiveGridBreakpoint,
	useRowVirtualizer,
} from '@shared/lib/hooks';

import { useSection } from '../sections.hooks';

const SECTION_ID = 14;
// Enough rows to fill a typical viewport at every grid breakpoint this
// section renders at (1/2/3 columns) without needing to know the real
// issue count up front -- this is a loading placeholder, not a page of
// real content, so it doesn't need to match the eventual item count.
const SKELETON_ISSUE_COUNT = 9;

export function Magazines() {
	const { isLoading, issues, error } = useSection(SECTION_ID);
	if (isLoading) {
		return (
			<section className={styles['magazines']}>
				<MagazinesSkeleton />
			</section>
		);
	}
	if (error) {
		return <div>{error.message}</div>;
	}

	return (
		<section className={styles['magazines']}>
			<IssuesContainer issues={issues} />
		</section>
	);
}

/**
 * Grid-shaped placeholder for the section's own data-loading state (the
 * `useSection` request, not yet resolved) -- mirrors `IssuesContainer`'s
 * real DOM shape (`.issues-container` split pane, `.latest-issue` +
 * `.issues` grid of `.issue` cards) using the same CSS classes, so the
 * skeleton lays out at every breakpoint exactly where the real content
 * will land instead of being replaced by a differently-shaped one
 * (user-reported 2026-08-27: blank/unstyled loading state on this screen).
 */
function MagazinesSkeleton() {
	return (
		<div className={styles['issues-container']} aria-busy="true">
			<div>
				<div className={styles['latest-issue']}>
					<figure>
						<Skeleton
							style={{ aspectRatio: '9 / 12', width: 'clamp(200px, 85vw, 500px)' }}
							className="bg-gray-300"
						/>
					</figure>
					<div className={styles['title']}>
						<Skeleton className="h-5 w-2/3 mx-auto bg-gray-300" />
						<Skeleton className="h-4 w-1/2 mx-auto mt-2 bg-gray-300" />
					</div>
				</div>
			</div>
			<div>
				<div className={styles['issues']}>
					{Array.from({ length: SKELETON_ISSUE_COUNT }).map((_, i) => (
						<div key={i} className={styles['issue']}>
							<div>
								<Skeleton
									style={{ aspectRatio: '9 / 12' }}
									className="bg-gray-300"
								/>
							</div>
							<div>
								<Skeleton className="h-4 w-3/4 bg-gray-300" />
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

export function IssuesContainer({ issues }: IssuesProps) {
	if (!issues) {
		return null;
	}
	const latestIssue = issues[0];
	return (
		<div className={styles['issues-container']}>
			<div>{latestIssue ? <LatestIssue {...latestIssue} /> : null}</div>
			<div>
				<Issues issues={issues} />
			</div>
		</div>
	);
}

interface IssuesProps {
	issues: Array<Issue>;
}

function LatestIssue(issue: Issue) {
	const { sendEvent } = useContext(TrackingContext);
	const { id, name, cover, description, slug } = issue;
	const from = `${window.location.pathname}?section=issues`;
	const transitionName = previewTransitionName(slug ?? id);

	return (
		<div className={styles['latest-issue']}>
			<PreviewLink
				to="/preview/$id"
				params={{ id: `${slug ?? id}` }}
				from={from}
				payload={{ preview: { imageSrc: cover, transitionName } }}
				onClick={() => {
					sendEvent({
						type: 'BTN_CLICK_ISSUE',
						data: {
							issue: {
								id: parseInt(`${issue.id}`, 10),
								slug: issue.slug,
							},
						},
					});
				}}
			>
				<figure style={{ cursor: 'pointer' }}>
					<CoverImage
						src={cover}
						alt={`${name} - Cover`}
						width={340}
						fetchPriority="high"
						style={{ viewTransitionName: transitionName }}
					/>
				</figure>
			</PreviewLink>
			<div className={styles['title']}>
				<h1>{name}</h1>
				{description ? <p>{description}</p> : null}
			</div>
		</div>
	);
}

function Issues({ issues }: IssuesProps) {
	const { isTablet, isDesktopPanel } = useResponsiveGridBreakpoint();

	if (isDesktopPanel) {
		// Unchanged from before virtualization -- `.issues-container` becomes
		// its own `overflow: auto` panel at this breakpoint (matches
		// basic/home/magazine/magazine.tsx's equivalent decision; see that
		// file's comment), and the grid's own CSS hides the first issue here
		// anyway (`LatestIssue` above already shows it).
		return (
			<div className={styles['issues']}>
				{issues.map((issue, i) => (
					<IssueComponent key={issue.id} {...issue} isFirst={i === 0} />
				))}
			</div>
		);
	}

	return <VirtualizedIssues issues={issues} columns={isTablet ? 3 : 2} />;
}

/**
 * `improvements/perf-baselines/10-virtualization-benchmark.md`. Same
 * mechanics as `poets/home/magazines/magazines.tsx`'s equivalent (this
 * template's grid is structurally identical to poets'/padi's) via
 * `@shared/lib/hooks/use-row-virtualizer.ts` -- `featureFirstRow: true`
 * because at tablet width the first issue lays out as a full-width
 * "feature" (`grid-template-areas: 'feature feature feature' '. . .'` in
 * magazines.module.css), not just another grid cell.
 */
function VirtualizedIssues({
	issues,
	columns,
}: {
	issues: Array<Issue>;
	columns: number;
}) {
	const { containerRef, totalSize, rows, measureElement } = useRowVirtualizer(
		{ itemCount: issues.length, columns, featureFirstRow: true },
		// One issue card's rough height at this breakpoint -- a starting
		// guess only, corrected against each row's real rendered height by
		// the hook's dynamic measurement.
		{ estimateRowHeight: 420 },
	);

	return (
		<div
			className={styles['issues']}
			ref={containerRef}
			style={{ display: 'block', position: 'relative', height: totalSize }}
		>
			{rows.map((row) => {
				// The feature row is always exactly one item (index 0) --
				// everything else is a uniform `columns`-sized grid row.
				const isFeatureRow = row.itemIndices[0] === 0;
				const rowStyle: React.CSSProperties =
					!isFeatureRow && columns > 1
						? { display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)` }
						: {};
				return (
					<div
						key={row.key}
						ref={measureElement}
						{...row.rowProps}
						style={{ ...row.style, ...rowStyle }}
					>
						{row.itemIndices.map((i) => (
							<IssueComponent
								key={issues[i].id}
								{...issues[i]}
								isFirst={i === 0}
							/>
						))}
					</div>
				);
			})}
		</div>
	);
}

// `isFirst` replaces this file's own `.issue:first-of-type` CSS rules
// (magazines.module.css, now `.issue[data-first-issue]`) -- see poets'
// magazines.tsx (the reference implementation) for why virtualization
// needs this instead.
const IssueComponent = (issue: Issue & { isFirst?: boolean }) => {
	const { sendEvent } = useContext(TrackingContext);
	const { id, name, description, cover, slug, isFirst } = issue;

	const from = `${window.location.pathname}?section=issues`;
	const transitionName = previewTransitionName(slug ?? id);
	return (
		<div className={styles['issue']} data-first-issue={isFirst || undefined}>
			<div>
				<PreviewLink
					to="/preview/$id"
					params={{ id: `${slug ?? id}` }}
					from={from}
					payload={{ preview: { imageSrc: cover, transitionName } }}
					onClick={() => {
						sendEvent({
							type: 'BTN_CLICK_ISSUE',
							data: {
								issue: {
									id: parseInt(`${issue.id}`, 10),
									slug: issue.slug,
								},
							},
						});
					}}
				>
					<figure style={{ cursor: 'pointer' }}>
						<CoverImage
							loading="lazy"
							src={cover}
							alt={`${name} - cover`}
							width={340}
							style={{ viewTransitionName: transitionName }}
						/>
					</figure>
				</PreviewLink>
			</div>
			<div>
				<h4>{name}</h4>
				<p>{description}</p>
			</div>
		</div>
	);
};

/**
 * Every cover `<img>` in this grid used to render with no loading state of
 * its own -- a lazy-loaded image sat as a blank white box (not even the
 * browser's default broken-image icon, since nothing had failed, it just
 * hadn't scrolled into view/finished fetching yet) until it happened to
 * finish (user-reported 2026-08-27). Tracks its own `onLoad`/`onError` and
 * shows the same shimmer used for the section's own `MagazinesSkeleton`
 * behind it until then, then stops -- deliberately not a permanent CSS-only
 * background (that would keep animating forever behind an already-loaded,
 * opaque cover).
 *
 * **First attempt at this broke the grid entirely** (user-reported
 * 2026-08-27, same day): hiding the unloaded `<img loading="lazy">` via
 * `display: none` gives it no layout box at all, and the browser's native
 * lazy-load only ever starts fetching an image once it *intersects the
 * viewport* -- an element with no box can never intersect anything, so
 * every grid image (this component is also used by the eager, non-lazy
 * `LatestIssue` cover, which was unaffected) got stuck "not loaded"
 * forever, collapsing the grid's row-height measurement along with it.
 * Fixed: the `<img>` always stays in normal flow (so lazy-load's own
 * intersection check keeps working); the shimmer is a `position: absolute`
 * sibling layered on top of it via a `position: relative` wrapper, covering
 * the still-loading image without touching its layout box.
 */
function CoverImage({ alt, style, ...rest }: React.ComponentProps<'img'>) {
	const [loaded, setLoaded] = useState(false);
	return (
		<span style={{ position: 'relative', display: 'block' }}>
			<img
				{...rest}
				alt={alt}
				style={style}
				onLoad={() => setLoaded(true)}
				onError={() => setLoaded(true)}
			/>
			{!loaded ? (
				<Skeleton
					aria-hidden="true"
					className="absolute inset-0 bg-gray-300"
				/>
			) : null}
		</span>
	);
}
